// SourceHeaderV3 — top strip with name, status, live stats, settings link.
//
// Vision: operator should always know "where am I + is data flowing".
// Stats auto-tick (last-event-ago) so the live indicator feels alive.

import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Source } from '@datahub/shared';
import { archiveSource, patchSource } from '../../api';
import { useToast } from '../Toast';

function fmtAgo(iso: string | null | undefined): string {
  if (!iso) return 'no events yet';
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return iso;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function SourceHeaderV3({ source, eventCount, errorCount, lastEventAt, onSourceChange, onOpenReplay }: {
  source: Source;
  eventCount: number | null;
  errorCount: number;
  lastEventAt: string | null;
  onSourceChange: (s: Source) => void;
  onOpenReplay: () => void;
}): React.ReactElement {
  const toast = useToast();
  const navigate = useNavigate();
  // Tick once a second so "12s ago" advances without a parent re-render.
  const [, setTick] = useState(0);
  useEffect(() => {
    const h = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(h);
  }, []);

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(source.name);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click.
  useEffect(() => {
    if (!menuOpen) return;
    function onDoc(e: MouseEvent): void {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  async function commitRename(): Promise<void> {
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === source.name) {
      setNameDraft(source.name);
      setEditingName(false);
      return;
    }
    try {
      const updated = await patchSource(source.source_id, { name: trimmed });
      onSourceChange(updated);
      toast.show('Source renamed.', { kind: 'success' });
    } catch (e) {
      toast.show(`Rename failed: ${(e as Error).message}`, { kind: 'error' });
      setNameDraft(source.name);
    } finally {
      setEditingName(false);
    }
  }

  async function archiveAndLeave(): Promise<void> {
    const ok = await toast.confirm({
      title: 'Archive this source?',
      message: `${source.name} will be archived. The webhook + upload endpoints will stop accepting new data. Existing events in GCS + BigQuery stay untouched — you can restore the source from the Archive page.\n\nType the source name to confirm.`,
      destructive: true,
      confirmLabel: 'Archive',
      requireType: source.name,
    });
    if (!ok) return;
    try {
      await archiveSource(source.source_id);
      toast.show('Source archived.', { kind: 'success' });
      navigate('/sources');
    } catch (e) {
      toast.show(`Archive failed: ${(e as Error).message}`, { kind: 'error' });
    }
  }

  const live = lastEventAt && (Date.now() - Date.parse(lastEventAt) < 60_000);

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap',
      padding: '0 0 14px',
      borderBottom: '1px solid var(--line)',
      marginBottom: 18,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          {editingName ? (
            <input
              autoFocus
              value={nameDraft}
              onChange={e => setNameDraft(e.target.value)}
              onBlur={() => { void commitRename(); }}
              onKeyDown={e => {
                if (e.key === 'Enter') { void commitRename(); }
                if (e.key === 'Escape') { setNameDraft(source.name); setEditingName(false); }
              }}
              style={{
                margin: 0, fontSize: 22, fontWeight: 600,
                fontFamily: 'var(--ui)', color: 'var(--ink)',
                background: 'var(--bg-elev)', border: '1px solid var(--accent-line)', borderRadius: 4,
                padding: '2px 6px',
                minWidth: 240,
              }}
            />
          ) : (
            <h1
              onDoubleClick={() => { setNameDraft(source.name); setEditingName(true); }}
              title="Double-click to rename"
              style={{ margin: 0, fontSize: 22, fontWeight: 600, color: 'var(--ink)', cursor: 'text' }}
            >{source.name}</h1>
          )}
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 11, fontFamily: 'var(--mono)',
            padding: '2px 8px', borderRadius: 12,
            background: live ? 'oklch(0.95 0.10 145)' : 'var(--bg-sunken)',
            color: live ? 'oklch(0.36 0.14 145)' : 'var(--ink-3)',
            border: `1px solid ${live ? 'oklch(0.78 0.10 145)' : 'var(--line)'}`,
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: '50%',
              background: live ? 'oklch(0.62 0.18 145)' : 'var(--ink-3)',
              animation: live ? 'pulse 1.6s ease-in-out infinite' : 'none',
            }} />
            {live ? 'live' : 'idle'}
          </span>
        </div>
        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>
          <button
            type="button"
            onClick={() => { void navigator.clipboard.writeText(source.source_id); toast.show('Source ID copied', { kind: 'success' }); }}
            title={`${source.source_id}\n\nClick to copy.`}
            style={{
              fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--ink-3)',
              background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
            }}
          >
            <code>{source.source_id.length > 20 ? `${source.source_id.slice(0, 16)}…` : source.source_id}</code>
          </button>
          {source.slug_alias && <code style={{ padding: '1px 6px', background: 'var(--accent-soft)', border: '1px solid var(--accent-line)', color: 'var(--accent)', borderRadius: 3 }}>/h/{source.slug_alias}</code>}
          <span>·</span>
          <span title="Events received in the last 24 hours (BigQuery COUNT — true count, not capped)">
            {eventCount === null ? '…' : `${eventCount.toLocaleString()} event${eventCount === 1 ? '' : 's'} · 24h`}
          </span>
          {errorCount > 0 && (
            <>
              <span>·</span>
              <span style={{ color: 'var(--danger)' }} title="Automation failures in the last 24 hours — click the Errors panel below to review">{errorCount} error{errorCount === 1 ? '' : 's'}</span>
            </>
          )}
          <span>·</span>
          <span>last event {fmtAgo(lastEventAt)}</span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          type="button"
          className="btn sm ghost"
          onClick={onOpenReplay}
          style={{ padding: '4px 12px', fontSize: 11.5 }}
          title="Replay events through current triggers — re-fire processing for a date range"
          aria-label="Replay events"
        ><span aria-hidden="true">↻</span> Replay</button>
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setMenuOpen(o => !o)}
            className="btn sm ghost"
            style={{ padding: '4px 12px', fontSize: 11.5 }}
            title="Source settings"
            aria-label="Open source settings menu"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          ><span aria-hidden="true">⚙</span> Settings</button>
          {menuOpen && (
            <div style={{
              position: 'absolute', right: 0, top: '100%', marginTop: 4,
              background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 6,
              boxShadow: '0 4px 12px oklch(0.18 0.01 250 / 0.18)',
              minWidth: 200, zIndex: 20,
              display: 'flex', flexDirection: 'column',
              padding: 4,
            }}>
              <button
                type="button"
                onClick={() => { setMenuOpen(false); setNameDraft(source.name); setEditingName(true); }}
                style={menuItemStyle}
              >Rename source</button>
              <Link
                to={`/sources/${source.source_id}?v=2`}
                onClick={() => setMenuOpen(false)}
                style={{ ...menuItemStyle, textDecoration: 'none', display: 'block' }}
                title="Switch to legacy V2 layout (file browser, full trigger CRUD with version history, errors-tab drill-down)"
              >Advanced settings →</Link>
              <Link
                to={`/audit?action_prefix=source.&resource_id=${source.source_id}`}
                onClick={() => setMenuOpen(false)}
                style={{ ...menuItemStyle, textDecoration: 'none', display: 'block' }}
                title="Audit log filtered to this source"
              >Audit log →</Link>
              <div style={{ borderTop: '1px solid var(--line)', margin: '4px 0' }} />
              <button
                type="button"
                onClick={() => { setMenuOpen(false); void archiveAndLeave(); }}
                style={{ ...menuItemStyle, color: 'var(--danger)' }}
              >Archive source…</button>
            </div>
          )}
        </div>
      </div>
      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1 } 50% { opacity: 0.35 } }
      `}</style>
    </div>
  );
}

const menuItemStyle: React.CSSProperties = {
  textAlign: 'left',
  background: 'transparent',
  border: 'none',
  padding: '8px 12px',
  fontSize: 12.5,
  color: 'var(--ink)',
  cursor: 'pointer',
  borderRadius: 4,
  fontFamily: 'var(--ui)',
};
